import type { UiTagColor } from '../../atoms/tag/tag.component';
import type { UiSegmentedFilterOption } from '../../molecules/segmented-filter/segmented-filter.component';

export type ColumnType =
  | 'string'
  | 'number'
  | 'currency'
  | 'date'
  | 'boolean'
  | 'link'
  | 'avatar'
  | 'projects'
  | 'string-list'
  | 'tags'
  | 'button';

/** Ítem de una celda `type: 'tags'`. */
export type DataTableTagEstatus = 'Activo' | 'Pendiente' | 'Inactivo';

export interface DataTableTagItem {
  label: string;
  estatus: DataTableTagEstatus;
}

/** Botón solo icono en una celda de `ui-data-table`. */
export interface DataTableIconButton {
  id: string;
  iconSrc?: string;
  ariaLabel: string;
  /** Usa el átomo compacto `ui-icon-action`, como en listas de comprobantes. */
  actionIcon?: 'edit' | 'delete' | 'view' | 'download';
  /** Muestra el SVG a color (`img`) en lugar de la máscara de `ui-button`. */
  asImage?: boolean;
}

/** Separador interno al emitir filtros de columna `multiselect` como string. */
export const DATA_TABLE_MULTI_FILTER_SEP = '\u001f';

/** Parsea el valor emitido por un filtro de columna `multiselect`. */
export function parseColumnMultiFilter(value: string | null | undefined): string[] {
  if (!value) return [];
  return value.split(DATA_TABLE_MULTI_FILTER_SEP).filter(Boolean);
}

/**
 * Editor usado en la celda cuando la tabla tiene edición inline.
 * `amount` usa el input de importes (máscara con miles y 2 decimales).
 */
export type DataTableEditType = 'text' | 'number' | 'amount' | 'date' | 'select';

/**
 * Cómo se resuelve el ancho de las columnas.
 * - `auto`: mide el contenido y solo reduce columnas con `truncate`.
 * - `fixed`: usa `width`; sin `truncate`, puede crecer para mostrar el contenido.
 */
export type DataTableColumnSizing = 'auto' | 'fixed';

export interface DataGridColumn {
  key: string;
  header: string;
  type: ColumnType;
  nullable?: boolean;
  sortable?: boolean;
  /**
   * Color semántico del `ui-tag` según el valor de la celda.
   * Si no se declara, `cellClassMap` continúa funcionando por compatibilidad.
   */
  tagColorMap?: Record<string, UiTagColor>;
  /** @deprecated Para estados nuevos usa `tagColorMap`. */
  cellClassMap?: Record<string, string>;
  trueLabel?: string;
  falseLabel?: string;
  linkKey?: string;
  linkLabel?: string;
  avatarKey?: string;
  tooltipByKey?: string;
  tooltipMap?: Record<string, string>;
  /**
   * Ancho en píxeles. Solo se usa con `columnSizing="fixed"`;
   * si falta, la columna toma 150px.
   */
  width?: number;
  /**
   * Ancho mínimo en píxeles durante el ajuste automático. Cuando la suma de
   * mínimos cabe en el contenedor, las columnas con `truncate` se reducen sin
   * generar scroll.
   */
  minWidth?: number;
  /**
   * Permite reducir la columna y mostrar elipsis. Por defecto el contenido se
   * conserva completo y la columna no se reduce por debajo de su ancho natural.
   */
  truncate?: boolean;
  /**
   * Peso con el que esta columna recibe espacio horizontal sobrante en
   * `columnSizing="fixed"`. Las columnas con mayor peso crecen más. Si ninguna
   * lo declara, se conserva el comportamiento histórico y crece la última.
   */
  grow?: number;
  /**
   * Fija la columna al borde izquierdo durante el scroll horizontal.
   * Opt-in; las columnas sin esta propiedad conservan el comportamiento actual.
   */
  sticky?: 'left';
  /** Control del filtro bajo el header. Default: `'text'`. `'none'` oculta el buscador. */
  filterType?: 'text' | 'select' | 'multiselect' | 'none';
  /**
   * Celda con saltos de línea (`white-space: pre-line`) y fila a altura auto.
   * Para valores unidos con salto de línea (tipo de cálculo, asuntos).
   */
  wrap?: boolean;
  /** Opciones cuando `filterType` es `'select'` o `'multiselect'`. */
  filterOptions?: DataTableToolbarSelectOption[];
  /** En columnas `tags` o `string`, el contenido emite `tagClick`. */
  clickable?: boolean;
  /**
   * Permite editar la celda cuando la tabla tiene `editable`.
   * En `type: 'boolean'`, pinta `ui-checkbox` y un check de columna en el header
   * (opt-in; no cambia las columnas boolean que siguen en Sí/No).
   */
  editable?: boolean;
  /** En la fila borrador, sin valor el botón Guardar queda deshabilitado. */
  required?: boolean;
  /** Editor inline. Por defecto se deriva de `type`. */
  editType?: DataTableEditType;
  /** Editor inline según la fila. Tiene prioridad sobre `editType`. */
  editTypeForRow?: (row: unknown) => DataTableEditType;
  /** Placeholder del editor inline de texto, número o importe. */
  editPlaceholder?: string;
  /** Opciones cuando el editor inline es `'select'`. Si falta, usa `filterOptions`. */
  editOptions?: DataTableToolbarSelectOption[];
  /** Opciones del select según la fila (p. ej. unidades del insumo). Tiene prioridad sobre `editOptions`. */
  editOptionsForRow?: (row: unknown) => DataTableToolbarSelectOption[];
  /** Claves a vaciar al cambiar esta celda (p. ej. unidad al cambiar insumo). */
  editResets?: string[];
  /** Tras elegir una opción, mueve el foco al editor de esta columna en la misma fila. */
  editAdvancesTo?: string;
  /** Texto del `ui-button` cuando `type` es `'button'`. Si falta, usa `header`. */
  buttonLabel?: string;
  /** Etiqueta del botón según la fila. Tiene prioridad sobre `buttonLabel`. */
  buttonLabelForRow?: (row: unknown) => string;
  /**
   * Varios botones ghost en la misma celda. Si hay ítems, sustituye el botón único.
   * Opt-in: las columnas sin este campo no cambian.
   */
  buttonLabels?: readonly string[];
  /**
   * Botones solo icono (`ui-button variant="icon"`) en la misma celda.
   * Opt-in: las columnas sin este campo no cambian.
   */
  iconButtons?: readonly DataTableIconButton[];
  /** Si retorna `false`, el botón de la celda no se muestra. */
  buttonVisible?: (row: unknown) => boolean;
  /**
   * Si retorna `false`, la celda no pinta check ni tags.
   * Opt-in: sin este campo la columna no cambia.
   */
  cellVisible?: (row: unknown) => boolean;
}

/** Cambio de una celda editable en una fila ya existente. */
export interface DataTableCellChange {
  row: unknown;
  key: string;
  value: string | number | boolean | null;
}

/** Check de columna en `type: 'boolean'` con `editable`. */
export interface DataTableColumnCheckChange {
  key: string;
  checked: boolean;
}

/** Clic en un tag o en una celda `string` configurada como clickable. */
export interface DataTableTagClick {
  row: unknown;
  key: string;
  tag: DataTableTagItem;
  /** Celda que recibió el clic, para anclar un panel. */
  origin?: HTMLElement;
}

export interface FilterValue {
  name: string;
  value: string;
  selected?: boolean;
}

export interface FilterOptionsModel {
  prefix: string;
  name: string;
  values: FilterValue[];
}

export interface RowAction {
  key: string;
  label: string;
  destructive?: boolean;
  /** Si retorna `false`, la acción no se muestra para esa fila. */
  visible?: (row: unknown) => boolean;
}

export interface DataTableToolbarSelectOption {
  id: string | number;
  label: string;
  value?: string | number | null;
  disabled?: boolean;
}

export interface DataTableToolbarSelect {
  key: string;
  /** Nombre corto del campo — se usa en el tooltip (ej. "Período"). */
  label?: string;
  /** Texto del placeholder cuando no hay selección (ej. "Período - Todos"). */
  hint: string;
  options: DataTableToolbarSelectOption[];
  selected?: DataTableToolbarSelectOption[];
  /** Ancho del trigger en px. Por defecto 240. */
  width?: number;
  /** Tipo de control. 'select' = selección única; 'multiselect' = selección múltiple (default). */
  type?: 'select' | 'multiselect';
}

/** Grupo de opciones exclusivas mostrado en el toolbar de `ui-data-table`. */
export interface DataTableSegmentedFilter {
  /** Clave estable para identificar el grupo en los cambios emitidos. */
  key: string;
  /** Nombre accesible del grupo. */
  label: string;
  /** Valor actualmente seleccionado. */
  value: string;
  options: readonly UiSegmentedFilterOption[];
}

export interface SortState {
  sortBy: string;
  order: 'asc' | 'desc';
}
